/**
 * The launch promotion, in one place.
 *
 * ---------------------------------------------------------------------------
 * THIS MUST NOT OUTLIVE THE STRIPE CODE
 * ---------------------------------------------------------------------------
 * `endsAt` mirrors the `expires_at` on the live Stripe promotion code. That is
 * the whole reason it exists as a date rather than a boolean: a hand-flipped
 * flag gets forgotten, and the failure mode is the worst kind — a homepage
 * advertising a discount code that Stripe now rejects at checkout. Tying the two
 * together means the bar disappears on its own the moment the code stops working.
 *
 * If the Stripe expiry is extended, change it here too.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CODE ACTUALLY DOES, WHICH THE COPY HAS TO RESPECT
 * ---------------------------------------------------------------------------
 * The Stripe coupon behind FIRST20OFF is `duration: once` and
 * `restrictions.first_time_transaction: true`. So:
 *
 *   - It discounts the FIRST invoice only, not every renewal. On an annual plan
 *     that is 20% off a full year, which is worth saying plainly; on monthly it
 *     is one month. The copy therefore says "your first payment" and never
 *     implies an ongoing discount.
 *   - It only applies to a customer who has not been charged before. The bar's
 *     call to action is aimed at new visitors, which matches.
 *
 * Promotion codes are only accepted on SUBSCRIPTION checkout. The one-off packs
 * (session pack, build-tool pack, prior-art search) deliberately do not accept
 * them, so "any plan" is accurate and must not become "anything".
 */

export interface LaunchPromo {
  /** Master switch. Set false to pull the bar immediately. */
  enabled: boolean;
  /** The code a visitor types at checkout. */
  code: string;
  /** Percentage off, for the copy. */
  percentOff: number;
  /** Unix ms. Mirrors the Stripe promotion code's `expires_at`. */
  endsAt: number;
  /** Bumped to re-show the bar to people who dismissed a previous promo. */
  version: number;
}

export const LAUNCH_PROMO: LaunchPromo = {
  enabled: false,
  code: 'FIRST20OFF',
  percentOff: 20,
  // 01 Oct 2026, matching the live Stripe promotion code exactly.
  endsAt: Date.UTC(2026, 9, 1, 0, 0, 0),
  version: 1,
};

/** Whether the promo should be shown at all right now. */
export function isPromoLive(promo: LaunchPromo = LAUNCH_PROMO): boolean {
  return promo.enabled && Date.now() < promo.endsAt;
}

/**
 * Dismissal key, versioned so a future promo is not pre-dismissed for everyone
 * who declined this one. Bump `version` to re-show a genuinely new offer.
 *
 * Stored in `sessionStorage`, not `localStorage`. That is the whole difference
 * between "gone for this visit" and "gone forever": sessionStorage is scoped to
 * the browser tab and cleared when it closes, so a returning visitor sees the
 * offer again while it is still running.
 *
 * It was localStorage first, on the reasoning that nagging someone who already
 * declined is worse than losing an impression. True as far as it goes, but it
 * also meant the founder could not see their own homepage as a visitor sees it
 * without clearing storage by hand — and a launch promo that a returning visitor
 * never sees again is a promo working at a fraction of its reach.
 */
export function promoDismissKey(promo: LaunchPromo = LAUNCH_PROMO): string {
  return `entredash.promo.dismissed.v${promo.version}`;
}
